const findScrollableChildren = (element, selector = '.js-scroll-wrapper') => {
	return Array.from(element.querySelectorAll(selector));
};

// Debounce helper to prevent rapid successive updates
const debounce = (fn, delay) => {
	let timeoutId;
	return (...args) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn.apply(null, args), delay);
	};
};

// Helper to check actual computed visibility by display:none
const isActuallyHiddenByDisplay = (element) => {
	const computedStyle = window.getComputedStyle(element);
	return computedStyle.display === 'none';
};

const isElementEffectivelyHidden = (scrollableElement) => {
	// 1. Is the scrollableElement itself actually hidden by display:none?
	if (isActuallyHiddenByDisplay(scrollableElement)) {
		return true;
	}

	// 2. Is it inside a designated transition content area (e.g., accordion content)
	//    that is closing or already hidden?
	let currentElement = scrollableElement.parentElement;
	while (currentElement && currentElement !== document.body) {
		// Check for our JS hook class indicating an element that transitions visibility
		if (currentElement.classList.contains('js-accordion-transition-content')) {
			const style = window.getComputedStyle(currentElement);
			let inLeaveTransition = false;

			// Check for Vue's standard transition 'leave' classes more generically
			for (const cls of currentElement.classList) {
				if (cls.endsWith('-leave-active') || cls.endsWith('-leave-to')) {
					inLeaveTransition = true;
					break;
				}
			}

			if (style.display === 'none' || inLeaveTransition) {
				return true; // Hidden if display:none or actively in a leave transition
			}

			// If we found the js-accordion-transition-content element, and it's neither
			// display:none nor in a leave transition, then for the purpose of this check,
			// it's considered visible. We can stop traversing up this specific path.
			break;
		}
		currentElement = currentElement.parentElement;
	}

	// If no conditions for being hidden are met (neither the element itself
	// nor a relevant js-marked ancestor is hiding it)
	return false;
};

const applyScrollStyles = (element) => {
	// Check if element is fully hidden (not transitioning)
	if (isElementEffectivelyHidden(element)) {
		return;
	}

	element.style.display = 'flex';
	element.style.flexDirection = 'column';
	element.style.minHeight = '0';
};

const removeScrollStyles = (element) => {
	element.style.display = '';
	element.style.flexDirection = '';
	element.style.minHeight = '';
};

const getBackgroundColor = (element) => {
	let currentElement = element;
	let backgroundColor = 'rgb(255, 255, 255)'; // default fallback color

	// Walk up the DOM tree until we find an element with a background-color
	while (currentElement && currentElement !== document) {
		const computedStyle = window.getComputedStyle(currentElement);
		const bgColor = computedStyle.backgroundColor;

		// Check if the background color is actually set (not transparent or none)
		if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
			backgroundColor = bgColor;
			break;
		}

		currentElement = currentElement.parentElement;
	}

	// Convert any rgba() to rgb() by removing alpha channel
	backgroundColor = backgroundColor.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/, 'rgb($1, $2, $3)');

	return `linear-gradient(360deg, ${backgroundColor} 0%, ${backgroundColor.replace('rgb', 'rgba').replace(')', ', 0)')} 100%)`;
};

const createShadow = (element) => {
	const shadow = document.createElement('div');
	Object.assign(shadow.style, {
		position:      'absolute',
		width:         '100%',
		height:        '100%',
		top:           0,
		left:          0,
		pointerEvents: 'none',
	});
	const inner = document.createElement('div');
	Object.assign(inner.style, {
		position:   'absolute',
		width:      '100%',
		height:     '80px',
		bottom:     0,
		left:       0,
		background: getBackgroundColor(element),
	});
	shadow.append(inner);
	return shadow;
};

const updateShadow = (element, config) => {
	if (config.disableScrollShadow) {
		if (element._shadow) {
			element._shadow.style.display = 'none';
		}
		return;
	}

	const scrollHeight = element.scrollHeight;
	const clientHeight = element.clientHeight;
	const scrollTop = element.scrollTop;

	const isScrollable = scrollHeight > clientHeight;

	// Use a small buffer (1px) to account for floating point precision issues
	const scrollBottom = Math.ceil(scrollTop + clientHeight);
	const isAtScrollEnd = scrollBottom >= scrollHeight - 1;
	const isHidden = clientHeight === 0 && scrollHeight === 0;

	let shadow = element._shadow;

	// Show shadow only if we're scrollable and not at the end
	if (isScrollable && !isAtScrollEnd && !isHidden) {
		if (!shadow) {
			shadow = createShadow(element);
			element._shadow = shadow;
			element.append(shadow);
		}
		if (shadow.style.display !== 'block') {
			shadow.style.display = 'block';
		}
	} else if (shadow) {
		if (shadow.style.display !== 'none') {
			shadow.style.display = 'none';
		}
	}
};

const updateParentClasses = (el, scrollableElements) => {
	// First, remove styles from all previously styled parents
	const styledElements = el.querySelectorAll('[data-has-scroll-child]');
	styledElements.forEach(element => {
		removeScrollStyles(element);
		element.removeAttribute('data-has-scroll-child');
	});

	// Then add styles to parents of scrollable elements
	scrollableElements.forEach(scrollable => {
		// If the scrollable element itself is effectively hidden (e.g., inside a closed accordion),
		// then its parents should not be styled by 'applyScrollStyles' on its behalf.
		if (isElementEffectivelyHidden(scrollable)) {
			return; // Skip to the next scrollable element
		}

		let parent = scrollable.parentElement;
		while (parent && parent !== el) {
			applyScrollStyles(parent);
			parent.setAttribute('data-has-scroll-child', '');
			parent = parent.parentElement;
		}
	});
};

const getConfig = (binding) => {
	const defaultConfig = {
		target:              '.js-scroll-wrapper',
		disableScrollShadow: false,
	};

	if (!binding.value) return defaultConfig;

	// If it's a string, it's the target selector
	if (typeof binding.value === 'string') {
		return {
			...defaultConfig,
			target: binding.value,
		};
	}

	// If it's an object, merge with defaults
	return {
		...defaultConfig,
		...binding.value,
	};
};

export default {
	bind(el, binding) {
		// Get config and store it
		const config = getConfig(binding);
		el._config = config;

		// Create a debounced update function with the selector
		const debouncedUpdate = debounce(() => {
			// Always use current config target
			const scrollableElements = findScrollableChildren(el, el._config.target);
			updateParentClasses(el, scrollableElements);

			// Update shadows for all scrollable elements
			scrollableElements.forEach(element => {
				updateShadow(element, el._config);
			});
		}, 100);

		// Store the function for later use
		el._updateFn = debouncedUpdate;

		// Add scroll listeners to scrollable elements
		const handleScroll = (event) => {
			// Use requestAnimationFrame for smoother updates
			requestAnimationFrame(() => {
				updateShadow(event.target, el._config);
			});
		};

		el._handleScroll = handleScroll;

		// Function to attach scroll listeners
		const attachScrollListeners = () => {
			const scrollableElements = findScrollableChildren(el, el._config.target);
			scrollableElements.forEach(element => {
				// Store current scroll position to detect direction changes
				element._lastScrollTop = element.scrollTop;
				element.addEventListener('scroll', handleScroll, { passive: true });
			});
		};

		el._attachScrollListeners = attachScrollListeners;
		attachScrollListeners();

		// Set up a mutation observer that watches for DOM changes
		const observer = new MutationObserver(() => {
			debouncedUpdate();
			// Re-attach scroll listeners when DOM changes
			attachScrollListeners();
		});

		observer.observe(el, {
			childList:       true,
			subtree:         true,
			attributes:      true,
			attributeFilter: ['class'],
		});

		// Store observer for cleanup
		el._scrollObserver = observer;

		// Add window resize listener
		const handleWindowResize = () => {
			debouncedUpdate();
		};
		window.addEventListener('resize', handleWindowResize);
		el._handleWindowResize = handleWindowResize;

		// Initial update
		debouncedUpdate();

		// Add resize observer to handle dynamic content changes
		const resizeObserver = new ResizeObserver(debouncedUpdate);
		resizeObserver.observe(el);
		el._resizeObserver = resizeObserver;
	},

	update(el, binding) {
		const oldConfig = el._config;
		const newConfig = getConfig(binding);
		el._config = newConfig;

		// Check if target selector changed
		if (oldConfig.target !== newConfig.target) {
			// Remove scroll listeners from old elements
			const oldScrollableElements = findScrollableChildren(el, oldConfig.target);
			oldScrollableElements.forEach(element => {
				element.removeEventListener('scroll', el._handleScroll);
				if (element._shadow) {
					element._shadow.remove();
					element._shadow = null;
				}
			});

			// Add scroll listeners to new elements
			if (el._attachScrollListeners) {
				el._attachScrollListeners();
			}
		}

		// Update if scroll shadow config changed
		if (oldConfig.disableScrollShadow !== newConfig.disableScrollShadow) {
			const scrollableElements = findScrollableChildren(el, newConfig.target);
			scrollableElements.forEach(element => {
				updateShadow(element, newConfig);
			});
		}

		if (el._updateFn) {
			el._updateFn();
		}
	},

	unbind(el) {
		// Remove scroll listeners and shadows
		const scrollableElements = findScrollableChildren(el, el._config.target);
		scrollableElements.forEach(element => {
			element.removeEventListener('scroll', el._handleScroll);
			if (element._shadow) {
				element._shadow.remove();
				element._shadow = null;
			}
		});

		// Cleanup all observers
		if (el._scrollObserver) {
			el._scrollObserver.disconnect();
			delete el._scrollObserver;
		}

		if (el._resizeObserver) {
			el._resizeObserver.disconnect();
			delete el._resizeObserver;
		}

		// Remove window resize listener
		if (el._handleWindowResize) {
			window.removeEventListener('resize', el._handleWindowResize);
			delete el._handleWindowResize;
		}

		// Remove all styles
		const styledElements = el.querySelectorAll('[data-has-scroll-child]');
		styledElements.forEach(element => {
			removeScrollStyles(element);
			element.removeAttribute('data-has-scroll-child');
		});
	},
};
